import * as store from '@attain-apps/fe/web/main/lib/store'
import type * as ents from '@attain-apps/entities'

import type * as models from './models'
import { State } from './state'

/**
 * Actions for portal scenario dropdown UI state.
 */
export function getActionsConfig() {
  return {
    /* eslint-disable sort-keys */
    closeOverlay: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: state => ({ ...state, presentOverlay: false }),
    }),
    refreshScenarios: store.actions.request({
      input: store.actions.Data.NONE,
      output:
        store.actions.Data.asIs<readonly ents.core.admin.Scenario[]>(),
      reducers: {
        request: ({ state }) => State.startRefreshingScenarios(state),
        success: ({ state, data: { response } }) =>
          State.finishRefreshingScenarios(state, response),
        error: ({ state, data: { error } }) =>
          State.failRefreshingScenarios(state, error),
        cancellation: ({ state }) => State.cancelRefreshingScenarios(state),
      },
    }),
    displayOverlay: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: state => ({ ...state, presentOverlay: true }),
    }),
    openCreate: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: state => State.openCreate(state),
    }),
    openEdit: store.actions.standalone({
      data: store.actions.Data.asIs<{
        scenario: ents.core.admin.Scenario
      }>(),
      reducerDeprecated: (state, { scenario }) =>
        State.openEdit(state, scenario),
    }),
    editDraft: store.actions.standalone({
      data: store.actions.Data.asIs<
        Partial<ents.core.admin.Scenario.Input>
      >(),
      reducerDeprecated: (state, updates) => State.editDraft(state, updates),
    }),
    cancelEdit: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: state => State.cancelEdit(state),
    }),
    submitEdit: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<State.EditDialog.Mode>(),
      reducers: {
        request: ({ state }) => State.startSubmittingEdit(state),
        success: ({ state }) => State.finishSubmittingEdit(state),
        error: ({ state, data: { error } }) =>
          State.failSubmittingEdit(state, error),
        cancellation: ({ state }) => State.cancelSubmittingEdit(state),
      },
    }),
    openDelete: store.actions.standalone({
      data: store.actions.Data.asIs<{
        scenario: ents.core.admin.Scenario
      }>(),
      reducerDeprecated: (state, { scenario }) =>
        State.openDelete(state, scenario),
    }),
    cancelDelete: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducerDeprecated: state => State.cancelDelete(state),
    }),
    submitDelete: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<void>(),
      reducers: {
        request: ({ state }) => State.startSubmittingDelete(state),
        success: ({ state }) => State.finishSubmittingDelete(state),
        error: ({ state, data: { error } }) =>
          State.failSubmittingDelete(state, error),
        cancellation: ({ state }) => State.cancelSubmittingDelete(state),
      },
    }),
    moveToVisibility: store.actions.request({
      input: store.actions.Data.asIs<{
        scenarioId: ents.core.admin.Scenario.Id
        visibility: ents.core.admin.Scenario.Visibility
      }>(),
      output: store.actions.Data.asIs<void>(),
      reducers: {
        request: ({ state, data }) =>
          State.startMovingScenario(state, data.scenarioId),
        success: ({ state, data: { request } }) =>
          State.finishMovingScenario(state, request.scenarioId),
        error: ({ state, data: { error, request } }) =>
          State.failMovingScenario(state, request.scenarioId, error),
        cancellation: ({ state, data: { request } }) =>
          State.finishMovingScenario(state, request.scenarioId),
      },
    }),
    toggleSectionCollapsed: store.actions.standalone({
      data: ({
        section,
        sectionId,
      }: ExactlyOneOf<{
        section: models.Section
        sectionId: models.Section.Id
      }>) => sectionId ?? section?.id,
      reducerDeprecated: (state, sectionId) => ({
        ...state,
        sectionVisibilityLookup: {
          ...state.sectionVisibilityLookup,
          [sectionId]: !state.sectionVisibilityLookup[sectionId],
        },
      }),
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State>
}
